import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as React from 'react'

import { Button } from '../actions/button'
import { cn } from '../lib/cn'
import { IconClose } from '../lib/icons'
import { SECTION_TITLE_TYPE } from '../typography/typography'
import { EntryTitleContext } from '../typography/entry-title-context'

// The sheet the entrance opens its forms in: a modal dialog on Radix that rises from the bottom
// on a phone and stands centred from 960px (packages/ui/docs/cornice-d-ingresso.md).

const TABBABLE = 'a[href], button:not(:disabled), input:not(:disabled), select, textarea'

function focusedElement(): HTMLElement | null {
  const focused = document.activeElement
  return focused instanceof HTMLElement && focused !== document.body ? focused : null
}

const SHEET =
  'fixed inset-x-0 bottom-0 z-(--z-dialog) flex max-h-[calc(100dvh-var(--spacing-safe-top)-var(--spacing-entry-sheet-top))] flex-col rounded-t-sheet border border-b-0 border-border bg-background shadow-overlay outline-none entry:inset-auto entry:top-1/2 entry:left-1/2 entry:max-h-[calc(100dvh-var(--spacing-entry-sheet-top)*2)] entry:w-(--size-entry-column) entry:-translate-x-1/2 entry:-translate-y-1/2 entry:rounded-lg entry:border-b'

const SHEET_MOTION =
  'ease-entry data-[state=closed]:animate-out data-[state=closed]:duration-(--duration-entry-close) data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:duration-(--duration-entry-open) data-[state=open]:slide-in-from-bottom entry:data-[state=closed]:fade-out entry:data-[state=closed]:slide-out-to-bottom-4 entry:data-[state=open]:fade-in entry:data-[state=open]:slide-in-from-bottom-4'

const SCRIM_MOTION =
  'data-[state=closed]:animate-out data-[state=closed]:duration-(--duration-entry-close) data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:duration-(--duration-entry-scrim) data-[state=open]:fade-in'

export interface EntrySheetProps {
  open: boolean
  onClose: () => void
  /** Called after the exiting sheet has unmounted, so its owner can remove the content. */
  onClosed?: () => void
  /** The close button's accessible name. */
  closeLabel: string
  /** What the sheet is called while what it shows has no heading yet: the product's name. */
  label: string
  /** Whether the sheet may move: it rises, and a new view fades in. */
  animated: boolean
  /** Names the view on show: when it changes the new view fades in. */
  viewKey?: string
  /** Where the focus goes back to when whatever opened the sheet is no longer there. */
  returnTo: React.RefObject<HTMLElement | null>
  children?: React.ReactNode
}

export function EntrySheet({
  open,
  onClose,
  onClosed,
  closeLabel,
  label,
  animated,
  viewKey,
  returnTo,
  children,
}: EntrySheetProps) {
  const sheet = React.useRef<HTMLDivElement>(null)
  const openedFrom = React.useRef<HTMLElement | null>(null)
  // Before anything in the sheet takes the focus: its title does so as it mounts.
  React.useLayoutEffect(() => {
    if (open) openedFrom.current = focusedElement()
  }, [open])

  function ask(next: boolean) {
    if (next) return
    onClose()
  }

  function takeFocus(event: Event) {
    // Reached only when no title took it first: the sheet itself holds it, not its first field.
    event.preventDefault()
    sheet.current?.focus()
  }

  function giveFocusBack(event: Event) {
    event.preventDefault()
    const opener = openedFrom.current
    openedFrom.current = null
    const target = opener?.isConnected
      ? opener
      : returnTo.current?.querySelector<HTMLElement>(TABBABLE)
    target?.focus()
    onClosed?.()
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={ask}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-entry-scrim
          className={cn('fixed inset-0 z-(--z-veil) entry-scrim', animated && SCRIM_MOTION)}
        />
        <DialogPrimitive.Content
          ref={sheet}
          data-surface="ink"
          aria-modal="true"
          aria-label={label}
          aria-describedby={undefined}
          inert={!open}
          aria-hidden={!open || undefined}
          onOpenAutoFocus={takeFocus}
          onCloseAutoFocus={giveFocusBack}
          className={cn(SHEET, animated && SHEET_MOTION)}
        >
          <DialogPrimitive.Close asChild>
            <Button
              variant="outline"
              size="icon"
              className="absolute top-4 right-4"
              aria-label={closeLabel}
            >
              <IconClose aria-hidden="true" />
            </Button>
          </DialogPrimitive.Close>
          <div className="overflow-y-auto overscroll-contain px-5 pt-6 pb-[calc(1.5rem+var(--spacing-safe-bottom))] entry:p-8">
            <div key={viewKey} className={cn(animated && 'animate-entry-view')}>
              <EntryTitleContext.Provider value={EntrySheetTitle}>
                {children}
              </EntryTitleContext.Provider>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** The heading of whatever the sheet shows: it names the dialog, and takes the focus as it mounts —
 *  when the sheet opens, and again for every view or step that brings a new one. It stands inside an
 *  `EntryFrame`'s sheet, never outside; a `PageTitle` there is one already. */
export const EntrySheetTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => {
  const own = React.useRef<HTMLHeadingElement | null>(null)
  React.useEffect(() => {
    own.current?.focus({ preventScroll: true })
  }, [props.children])
  return (
    <DialogPrimitive.Title
      ref={(node) => {
        own.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref !== null) ref.current = node
      }}
      tabIndex={-1}
      data-entry-sheet-title=""
      className={cn(SECTION_TITLE_TYPE, 'pr-12 outline-none', className)}
      {...props}
    />
  )
})
EntrySheetTitle.displayName = 'EntrySheetTitle'
